import React from 'react';
import { Button, Alert } from 'rhinostyle';
import AuthService from '../services/AuthService';
import PageLoader from './PageLoader';
import StorageService from '../services/StorageService';
import { getLogoutMessage, shouldForceLoginFor } from '../constants/AuthConstants';
import rhinoLogoReversed from '../assets/images/rhinogram-logo-reversed.svg';

/**
 * /auth entry point for Auth0 users.
 *
 * With no `logout_message` in the query we go straight to Auth0. With one, we render the
 * message and a Log In button instead of auto-redirecting, so a failing login cannot loop
 * between this page and Auth0 and the user can actually read why they were sent back.
 *
 * Query params are read with URLSearchParams on purpose: LocationHelpers.getQueryParams
 * rewrites underscores to spaces, which would break keys like `access_denied`.
 */
class AuthLogin extends React.Component {
  state = {
    isLoading: true,
    logoutMessage: null,
  };

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const logoutMessage = params.get('logout_message');
    const path = params.get('path');

    // the default IDP being set
    StorageService.createEntry('auth-connection', process.env.REACT_APP_AUTH0_CONNECTION);

    if (path) {
      sessionStorage.setItem('redirect_url', path);
    }

    if (logoutMessage) {
      this.setState({ isLoading: false, logoutMessage });
    } else {
      this.handleLogin();
    }
  }

  handleLogin = () => {
    const { logoutMessage } = this.state;
    this.setState({ isLoading: true });
    // logout_message is forwarded to Auth0 as a custom authorize param for the Universal Login template.
    // When we are here because of an auth error, force a fresh credential prompt; otherwise Auth0's
    // existing session for the denied account just replays the same failure. A transient API
    // failure is the exception: the session is fine, so let Auth0 reuse it.
    AuthService.login(logoutMessage || undefined, { forceLogin: shouldForceLoginFor(logoutMessage) }).catch((err) => {
      console.error('Auth0 login redirect failed:', err); // eslint-disable-line no-console
      this.setState({ isLoading: false, logoutMessage: 'auth0error' });
    });
  };

  render() {
    const { isLoading, logoutMessage } = this.state;

    if (isLoading) {
      return (
        <div className="app-login-wrapper">
          <div className="app-login" data-vh="100">
            <PageLoader />
          </div>
        </div>
      );
    }

    return (
      <div className="app-login-wrapper">
        <div className="app-login" data-vh="100">
          <div className="login-redirect__container">
            <div className="app-login__logo">
              <img src={rhinoLogoReversed} alt="Rhinogram" />
            </div>
            <Alert solid type="danger" className="u-m-b-large u-text-center">
              {getLogoutMessage(logoutMessage)}
            </Alert>
            <Button
              onClick={this.handleLogin}
              type="outline-reversed"
              data-cypress="auth0LoginButton"
            >
              Log In
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default AuthLogin;
